import { Controller } from "@hotwired/stimulus"

// Live voice transport for every GPT-Live tool (AI Mock Interview, Interview
// Buddy). Owns the WebRTC session and does NO layout work: it turns server
// events into DOM CustomEvents dispatched from `this.element`, and the page
// reacts. Spec: docs/superpowers/specs/2026-09-11-gpt-live-interview-tools-design.md §4.
//
// Replaces mock_interview_realtime_controller.js. Every gotcha in that file is
// ported verbatim below -- each one cost a real debugging session -- but the
// handshake and the event vocabulary are GPT-Live's, not Realtime's:
//
//   - Rails is INSIDE the handshake now. GPT-Live has no ephemeral client
//     secrets, so we POST our SDP offer to our own endpoint and it returns the
//     answer. The browser never holds an OpenAI credential.
//   - Realtime's explicit response-trigger event does not exist here. The
//     assistant speaks first because we send ONE
//     `session.instructions.append` on `session.started`.
//   - Realtime's server-side voice-activity turn detection is gone, and there
//     is no end-of-turn event. Turn boundaries are derived in
//     #handleLiveEvent (see the event half of this file).
//   - There is no text input. A candidate who cannot use a microphone belongs
//     on the turn-based flow, not here.
//
// ── Event contract (dispatched from this.element; Stimulus prefix is the
//    registered identifier, "live-voice") ─────────────────────────────────
//   live-voice:connection-state  { state }  idle|connecting|connected|failed|disconnected|closed
//   live-voice:session-created   { sessionId, maxSessionSeconds, advancePrompts, liveQuestions }
//                                advancePrompts/liveQuestions are Buddy-specific (empty for the
//                                mock interview, whose endpoint returns neither). liveQuestions is
//                                the SAME filtered/truncated { id, question } list advancePrompts was
//                                built from -- Buddy drives its Next control off this list, not the
//                                raw question array, so the two stay index-aligned even when a
//                                malformed row is dropped from one but would otherwise linger in the
//                                other.
//   live-voice:channel-open      { open }
//   live-voice:speaking          { speaking }   the assistant is talking
//   live-voice:listening         { listening }  the candidate is talking
//   live-voice:caption-delta     { text }       assistant's live caption
//   live-voice:turn-committed    { role, content, conversation }
//   live-voice:mic-level         { level }      local RMS 0..1, ~10/sec
//   live-voice:mic-enabled       { enabled }
//   live-voice:uplink-stats      { bytesSent, deltaBytes, silent }
//   live-voice:time-remaining    { secondsRemaining, maxSessionSeconds }
//   live-voice:usage             { seconds }    server-reported, cross-check
//   live-voice:error             { message, context }
//                                context: session|media|webrtc|sdp|autoplay|server|unexpected
//   live-voice:ended             { reason }     user|session-cap|navigation|error|
//                                connection-failed|connection-closed|connection-lost|
//                                close_requested|<server reason>
//                                NEVER fired for "restart" -- see #teardown: restarting
//                                discards the previous call silently rather than
//                                announcing an end, since Tasks 9/10 bind to this event
//                                to score and persist the transcript and a restart is not
//                                a finished call.
//
// ── Public actions ──────────────────────────────────────────────────────
//   start(), end(), toggleMic(), append(event), flush()
//
// ── Values ──────────────────────────────────────────────────────────────
//   sessionUrl       our create endpoint
//   role, experienceLevel, focus   forwarded to the mock interview endpoint
//   buddySessionId   forwarded to the Buddy endpoint as session_id
export default class extends Controller {
  static targets = ["audio"]

  static values = {
    sessionUrl: { type: String, default: "/ai-mock-interview/live-session" },
    role: { type: String, default: "" },
    experienceLevel: { type: String, default: "" },
    focus: { type: String, default: "" },
    buddySessionId: { type: String, default: "" },
    greeting: { type: String, default: "" }
  }

  LOG_PREFIX = "[live-voice]"
  MIC_LEVEL_INTERVAL_MS = 100
  UPLINK_STATS_INTERVAL_MS = 2000
  SESSION_CAP_TICK_MS = 1000
  // How long to wait for session.closed after we send session.close, so final
  // usage is collected. A hung close must never leave the mic hot, so teardown
  // proceeds regardless once this elapses.
  CLOSE_GRACE_MS = 1500

  // WebRTC can flap to "disconnected" and recover on its own within a few
  // seconds -- a brief network hiccup, not a dead call. Tearing down the
  // instant we see it would kill calls that would otherwise have survived, so
  // we wait this long and only tear down if it has not returned to
  // "connected" by the time this elapses. "failed" and "closed" get no such
  // grace -- WebRTC only reports those once it has given up for good.
  DISCONNECT_GRACE_MS = 5000

  // Live has no end-of-turn event. A turn closes when the speaker changes, or
  // when the active transcript has been quiet this long — measured against the
  // deltas' own end_ms timestamps, not wall clock, which drifts under jitter.
  // Tuned in the browser pass; a value too low chops sentences in half, too
  // high merges a question and its answer into one turn.
  TURN_GAP_MS = 1200

  // Our own prompt asks the model for backchannels ("mm-hmm", "I see", "got
  // it") while the candidate is still answering. Every one of those is a
  // speaker change, and committing on every speaker change -- as a naive
  // implementation does -- shreds the candidate's ONE answer into fragments
  // around each interjection.
  //
  // The threshold applies to the INCOMING segment, never the outgoing one.
  // At the moment of a speaker change we have not heard the interruption
  // yet, so its nature is unknowable and the outgoing turn's own length says
  // nothing about it -- a long candidate answer interrupted by a short
  // backchannel is precisely the case the mechanism exists for, and gating
  // the hold on the outgoing length sent exactly that case down the
  // commit-immediately branch, splitting one answer into two. So the
  // outgoing turn is ALWAYS held (#onSpeakerChange) and the decision is made
  // once the incoming segment has shown what it is: under this many
  // characters with the original speaker resuming means backchannel (merge,
  // see #resolveHeldTurn); at or over it means a genuine turn, and the held
  // turn is released the instant the threshold is crossed
  // (#onTranscriptDelta) rather than waiting for the new turn to finish.
  MIN_TURN_CHARS = 12

  // NOTE: the Stimulus mount hook, NOT "connect the call". Starting a billed
  // session on page load without a user gesture would be wrong on both cost
  // and browser-autoplay-gesture grounds. start() begins the call.
  connect() {
    this.#resetCallState()

    // Turbo caches the page DOM before navigating away, and disconnect() is
    // too late for that snapshot -- a bfcache restore could otherwise
    // resurrect refs to closed connections and stopped tracks.
    this._onBeforeCache = () => this.#teardown("navigation")
    document.addEventListener("turbo:before-cache", this._onBeforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this._onBeforeCache)
    this.#teardown("navigation")
  }

  #resetCallState() {
    this.state = "idle"
    this.conversation = []
    this.micEnabled = true
    this.listening = false
    this.speaking = false
    this.torndown = false
    this.liveSessionId = null
    this.greetingSent = false

    this.pc = null
    this.dc = null
    this.micStream = null
    this.audioEl = null
    this.audioElCreatedByUs = false
    this.sender = null

    this.meterAudioContext = null
    this.micLevelTimer = null
    this.uplinkStatsTimer = null
    this.capTimer = null
    this.turnTimer = null
    this.disconnectGraceTimer = null
    // Cleared here too, not only in #teardown(): without this, a call that
    // restarts after a prior close-in-progress would report the FIRST call's
    // end reason for the new one.
    this.closeTimer = null
    this.pendingCloseReason = null
    this.secondsRemaining = null
    this.maxSessionSeconds = null
    this.lastUplinkBytes = 0

    // Turn-segmentation state; see the event half of this file.
    this.openTurn = null
    this.heldTurn = null
  }

  // ── Public actions ────────────────────────────────────────────────────

  async start() {
    if (this.state === "connecting" || this.state === "connected") {
      console.warn(this.LOG_PREFIX, "start() called while already", this.state, "— ignoring")
      return
    }

    // A previous call may have ended in "failed"/"disconnected"/"closed"
    // without ever tearing down -- the re-entry guard above only blocks
    // "connecting"/"connected". #resetCallState() only NULLS handles, it does
    // not release them, so without this the old microphone, peer connection,
    // AudioContext and every timer stay live and unreachable: the first mic
    // runs hot until the page unloads, and its orphaned cap timer keeps
    // counting toward tearing down the middle of the NEW call.
    if (this.pc || this.dc || this.micStream) this.#teardown("restart")

    // Reset per-CALL state here, not only in connect(): connect() runs once per
    // page load, so a second call without a reload would otherwise keep the
    // first call's turns and score both conversations as one session.
    // #resetCallState() runs AFTER #teardown() above and sets torndown back
    // to false, so the new call is never born already torn down.
    this.#resetCallState()
    this.#setConnectionState("connecting")

    // Which phase is in flight, so the single catch below reports the phase
    // that actually failed rather than a hardcoded one.
    let phase = "media"

    try {
      // EXACTLY ONCE. A second call for the same microphone re-opens the
      // device and leaves the first track alive but silent on macOS Chrome:
      // audio keeps encoding, the server receives a hiss of nothing, and
      // nothing downstream ever fires. Everything else borrows this.micStream.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      })
      this.micStream = stream
      const micTrack = stream.getAudioTracks()[0]
      console.log(this.LOG_PREFIX, "microphone acquired:", micTrack?.label || "unlabelled")
      this.#startMicMetering(stream)

      phase = "webrtc"
      const pc = new RTCPeerConnection()
      this.pc = pc
      this.#attachAudioElement(pc)
      pc.addTrack(micTrack, stream)

      // The channel name is fixed by the API, and it must exist BEFORE the
      // offer is created or the offer carries no data channel at all.
      const dc = pc.createDataChannel("oai-events")
      this.dc = dc
      dc.onopen = () => {
        console.log(this.LOG_PREFIX, "event channel open")
        this.dispatch("channel-open", { detail: { open: true } })
      }
      dc.onclose = () => this.dispatch("channel-open", { detail: { open: false } })
      dc.onmessage = (event) => {
        try {
          this.handleLiveEvent(JSON.parse(event.data))
        } catch (error) {
          console.warn(this.LOG_PREFIX, "bad event payload:", error)
        }
      }

      pc.onconnectionstatechange = () => {
        const connectionState = pc.connectionState
        console.log(this.LOG_PREFIX, "connection state:", connectionState)
        if (connectionState === "connected") {
          clearTimeout(this.disconnectGraceTimer)
          this.disconnectGraceTimer = null
          this.#setConnectionState("connected")
          this.#startSessionCapCountdown()
        } else if (connectionState === "failed") {
          // No grace period: WebRTC only reports "failed" once it has already
          // given up, so the mic must be released now, not left hot waiting
          // for a recovery that is not coming.
          this.dispatch("error", { detail: { message: "Voice connection failed.", context: "webrtc" } })
          this.#teardown("connection-failed", { finalState: "failed" })
        } else if (connectionState === "disconnected") {
          this.#setConnectionState("disconnected")
          // Give it a chance to reconnect on its own before treating the mic
          // as abandoned.
          clearTimeout(this.disconnectGraceTimer)
          this.disconnectGraceTimer = setTimeout(() => {
            if (this.pc?.connectionState !== "connected") {
              this.#teardown("connection-lost", { finalState: "disconnected" })
            }
          }, this.DISCONNECT_GRACE_MS)
        } else if (connectionState === "closed") {
          this.#teardown("connection-closed", { finalState: "closed" })
        }
      }

      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)

      // Our server proxies this to /v1/live/sessions with the standing key and
      // returns the answer. No OpenAI credential exists in this browser.
      phase = "session"
      const session = await this.#createSession(offer.sdp)
      this.liveSessionId = session.session_id
      this.maxSessionSeconds = session.max_session_seconds
      // Hand the page whatever else the endpoint returned. Interview Buddy
      // needs its advance prompts, which are built server-side from the one
      // tested source of that wording.
      this.dispatch("session-created", {
        detail: {
          sessionId: session.session_id,
          maxSessionSeconds: session.max_session_seconds,
          advancePrompts: session.advance_prompts || [],
          liveQuestions: session.live_questions || []
        }
      })

      phase = "sdp"
      await pc.setRemoteDescription({ type: "answer", sdp: session.answer_sdp })
      console.log(this.LOG_PREFIX, "WebRTC established — session is live")

      // Prove the mic is actually being transmitted. Flat bytes means the
      // problem is upstream of OpenAI (muted track, wrong device, no
      // permission) and no prompt tuning will help.
      this.sender = pc.getSenders().find((s) => s.track?.kind === "audio")
      this.#startUplinkStatsPolling()
    } catch (error) {
      console.error(this.LOG_PREFIX, `connect failed during ${phase}:`, error)
      this.dispatch("error", {
        detail: { message: error.message || "Failed to start the live session.", context: phase }
      })
      this.#teardown("error", { finalState: "failed" })
    }
  }

  end() {
    this.#closeAndTeardown("user")
  }

  toggleMic() {
    this.setMicEnabled(!this.micEnabled)
  }

  // Commits any open or held turn RIGHT NOW instead of waiting for its natural
  // TURN_GAP_MS quiet gap. Interview Buddy's Next control calls this before it
  // slices the transcript to file an answer -- without it, a segment still
  // mid-flight when the candidate clicks Next is missing from THIS question's
  // slice and, because the answer-start index has already moved on, lands
  // inside the NEXT question's slice once it finally commits on its own.
  // #flushTurns() dispatches turn-committed synchronously, so by the time this
  // method returns the caller's own transcript state already reflects it.
  flush() {
    this.#flushTurns()
  }

  setMicEnabled(enabled) {
    if (!this.micStream) return
    const on = Boolean(enabled)
    this.micStream.getAudioTracks().forEach((track) => { track.enabled = on })
    this.micEnabled = on
    // Mute server-side as well as locally: a muted local track still holds the
    // device, and the server should stop transcribing what we are not sending.
    this.#send({ type: on ? "session.input_audio.unmute" : "session.input_audio.mute" })
    this.dispatch("mic-enabled", { detail: { enabled: on } })
  }

  // ── Handshake helpers ─────────────────────────────────────────────────

  async #createSession(offerSdp) {
    const csrfMeta = document.querySelector('meta[name="csrf-token"]')
    const body = { sdp: offerSdp }
    if (this.roleValue) body.role = this.roleValue
    if (this.experienceLevelValue) body.experience_level = this.experienceLevelValue
    if (this.focusValue) body.focus = this.focusValue
    if (this.buddySessionIdValue) body.session_id = this.buddySessionIdValue

    const response = await fetch(this.sessionUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfMeta ? csrfMeta.content : "" },
      body: JSON.stringify(body)
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.success || !data.answer_sdp) {
      throw new Error(data.error || `Could not start the live session (HTTP ${response.status}).`)
    }
    return data
  }

  // The assistant's voice must land on an <audio> element attached to the
  // document -- Chrome will not reliably play a detached media element, which
  // yields a "connected but inaudible" session that looks healthy in logs.
  #attachAudioElement(pc) {
    if (this.hasAudioTarget) {
      this.audioEl = this.audioTarget
      this.audioElCreatedByUs = false
    } else {
      this.audioEl = document.createElement("audio")
      this.audioEl.style.display = "none"
      this.element.appendChild(this.audioEl)
      this.audioElCreatedByUs = true
    }
    this.audioEl.autoplay = true
    this.audioEl.setAttribute("playsinline", "")

    pc.ontrack = (event) => {
      console.log(this.LOG_PREFIX, "receiving assistant audio")
      this.audioEl.srcObject = event.streams[0]
      this.audioEl.play().catch((error) => {
        // Autoplay policy needs a user gesture. Surface it rather than failing
        // silently to a mute session.
        console.warn(this.LOG_PREFIX, "audio playback blocked:", error)
        this.dispatch("error", {
          detail: { message: "Click anywhere to enable audio.", context: "autoplay" }
        })
      })
    }
  }

  // ── Server events ─────────────────────────────────────────────────────
  // GPT-Live's vocabulary, which shares almost nothing with Realtime's. There
  // is no per-item "done" event and no response lifecycle: speaking and
  // listening are derived from which transcript is currently moving.
  handleLiveEvent(evt) {
    switch (evt.type) {
      case "session.started":
        console.log(this.LOG_PREFIX, "session started:", evt.session?.id || this.liveSessionId)
        this.#sendGreeting()
        break

      case "session.output_transcript.delta":
        this.#onTranscriptDelta("assistant", evt)
        break

      case "session.input_transcript.delta":
        this.#onTranscriptDelta("user", evt)
        break

      case "session.usage.updated": {
        const seconds = evt.usage?.seconds
        if (typeof seconds === "number") this.dispatch("usage", { detail: { seconds } })
        break
      }

      case "session.closed": {
        // close_requested is our own session.close being acknowledged. Every
        // other reason is the server ending the call on us, and the user is
        // told which -- an unexplained end reads as a crash.
        const reason = evt.reason || "close_requested"
        console.log(this.LOG_PREFIX, "session closed by server:", reason)
        this.#flushTurns()
        if (reason !== "close_requested") {
          this.dispatch("error", { detail: { message: this.#closeMessage(reason), context: "server" } })
        }
        clearTimeout(this.closeTimer)
        this.#teardown(this.pendingCloseReason || reason)
        break
      }

      case "error":
        console.error(this.LOG_PREFIX, "server error:", evt.error)
        this.dispatch("error", {
          detail: { message: evt.error?.message || "Live session error", context: "server" }
        })
        break

      default:
        // Surface anything unexpected. Silence here is what hid a dead input
        // path in the reference implementation.
        console.debug(this.LOG_PREFIX, "unhandled event:", evt.type)
        break
    }
  }

  #closeMessage(reason) {
    return {
      expired: "The session reached its time limit.",
      content: "The session was ended by a content filter.",
      remote_hangup: "The session was ended.",
      connection_lost: "The connection dropped."
    }[reason] || "The session ended unexpectedly."
  }

  // Live removed Realtime's explicit response-trigger event, so this append is
  // the ONLY thing that makes the assistant speak first. It fires from
  // session.started rather than any page timer -- a timer that fires before
  // the session starts does nothing and never retries, leaving the session
  // mute forever.
  #sendGreeting() {
    if (this.greetingSent) return
    if (!this.greetingValue) {
      // Nothing else makes the assistant speak first. A missing greeting
      // value -- a data attribute Task 9 or 10 forgot to set -- would
      // otherwise produce a session that looks perfectly healthy in every
      // log and every spec (connected, channel open, no error) and is simply
      // silent for the whole interview.
      console.warn(this.LOG_PREFIX, "no greeting configured -- the assistant will not speak first")
      this.dispatch("error", {
        detail: { message: "The interview could not be configured to start speaking.", context: "session" }
      })
      return
    }
    // `delegation_id` is REQUIRED on every context-append event, and `null` is
    // the documented value for session-wide context, which is what a greeting
    // is. Omitting it does not fail loudly at the transport level: the channel
    // is open and the send succeeds, then the server replies with an `error`
    // reading "Missing required parameter: 'delegation_id'" and the assistant
    // simply never speaks. Found in production, not by any test -- the
    // source-text spec asserts the event NAME, which was always right.
    if (this.#send({ type: "session.instructions.append", delegation_id: null, content: this.greetingValue })) {
      this.greetingSent = true
      console.log(this.LOG_PREFIX, "requested the opening greeting")
    }
  }

  // Steers the conversation mid-call (Interview Buddy's Next control). Bound
  // from a window event so a control outside this element can drive it.
  append(event) {
    const text = (event?.detail?.text ?? event?.params?.text ?? "").toString().trim()
    if (!text) return
    this.#send({ type: "session.instructions.append", delegation_id: null, content: text })
  }

  // ── Turn segmentation ─────────────────────────────────────────────────
  // The single most delicate thing in this file. Realtime committed turns for
  // us; Live streams deltas and never says a turn is over, so a bad boundary
  // silently corrupts the transcript that scoring consumes.
  #onTranscriptDelta(role, evt) {
    const delta = evt.delta || ""
    if (!delta) return

    // A speaker change never commits directly -- see #onSpeakerChange. Our own
    // prompt asks the model for backchannels mid-answer, and committing on
    // every speaker change would shred the other party's one answer around
    // each interjection. The outgoing turn is held instead; what it turns out
    // to be is decided below and in #resolveHeldTurn, once the interruption
    // has actually been heard.
    if (this.openTurn && this.openTurn.role !== role) this.#onSpeakerChange(role)

    const isNewTurn = !this.openTurn
    if (isNewTurn) this.openTurn = { role, text: "", deltaCount: 0 }
    this.openTurn.text += delta
    this.openTurn.deltaCount += 1

    // Stream time, not receipt time -- see #pollTurnGap. A quiet gap in the
    // audio itself is what ends a turn; a network stall that only delays
    // delivery of deltas whose end_ms keeps climbing must not.
    if (typeof evt.end_ms === "number") this.openTurn.endMs = evt.end_ms

    // The incoming segment has just proved itself a real turn rather than a
    // backchannel, so the turn held for the other speaker is genuinely over.
    // Released HERE, the moment the threshold is crossed, rather than at this
    // turn's own end: the held turn is already complete, and making the page
    // (and Buddy's Next control, which slices the transcript) wait out a whole
    // interviewer question before it sees the answer that preceded it would be
    // a needless lag. Nothing is lost by releasing early -- a turn released
    // here is simply committed in the order it was spoken.
    if (
      this.heldTurn &&
      this.heldTurn.role !== this.openTurn.role &&
      this.openTurn.text.trim().length >= this.MIN_TURN_CHARS
    ) {
      this.#releaseHeldTurn()
    }

    this.#setTalking(role)
    if (role === "assistant") {
      this.dispatch("caption-delta", { detail: { text: this.openTurn.text } })
    }

    // Armed once per open turn, not reset on every delta -- see #pollTurnGap
    // for why resetting on receipt would make the stream-time check vacuous.
    if (isNewTurn) this.#armTurnGapPoll()
  }

  // Wall clock here only sets HOW OFTEN we check; it never decides the turn
  // is over by itself. The decision is always made in #pollTurnGap by
  // comparing the open turn's end_ms now against what it was when this poll
  // was armed. Resetting this timer on every delta (as a first draft of this
  // file did) would make that comparison meaningless -- the armed baseline
  // would always equal the latest delta, so "did it advance" could never be
  // true when the poll actually fires.
  #armTurnGapPoll() {
    clearTimeout(this.turnTimer)
    const armedEndMs = this.openTurn?.endMs
    const armedDeltaCount = this.openTurn?.deltaCount
    this.turnTimer = setTimeout(() => this.#pollTurnGap(armedEndMs, armedDeltaCount), this.TURN_GAP_MS)
  }

  // If end_ms has moved since this poll was armed, real audio time has
  // elapsed and the speaker is still going -- keep polling rather than
  // commit. Only a turn whose end_ms has genuinely stopped moving is done. A
  // turn that never carried an end_ms (a payload shape we have not seen)
  // falls back to the delta count so it still commits eventually instead of
  // polling forever with nothing to compare.
  #pollTurnGap(armedEndMs, armedDeltaCount) {
    if (!this.openTurn) return

    const stillTalking = typeof armedEndMs === "number" && typeof this.openTurn.endMs === "number"
      ? this.openTurn.endMs > armedEndMs
      : this.openTurn.deltaCount > armedDeltaCount

    if (stillTalking) {
      this.#armTurnGapPoll()
      return
    }

    const finished = this.openTurn
    this.openTurn = null
    if (this.heldTurn) {
      // Nobody's speaker resumed before this gap elapsed -- the held
      // fragment and this turn are both genuine, separate turns.
      this.#resolveHeldTurn(finished, null)
    } else {
      this.#commitOpenTurn(finished)
    }
    this.#setTalking(null)
  }

  #setTalking(role) {
    const speaking = role === "assistant"
    const listening = role === "user"
    if (speaking !== this.speaking) {
      this.speaking = speaking
      this.dispatch("speaking", { detail: { speaking } })
    }
    if (listening !== this.listening) {
      this.listening = listening
      this.dispatch("listening", { detail: { listening } })
    }
  }

  // A speaker change never commits the outgoing segment directly, and never
  // decides anything about it either. Our own prompt asks the model for
  // backchannels ("mm-hmm") mid-answer, and at this instant the interruption
  // has not been heard yet -- so whether this is a genuine turn switch is
  // simply not knowable here, and the OUTGOING segment's length is no evidence
  // about it. (It was once used as evidence, which meant the mechanism's own
  // motivating case -- a long answer cut by a short backchannel -- took the
  // commit-immediately branch and was split into two user turns, roughly
  // doubling the turn count scoring reads.)
  //
  // So the outgoing segment is held unconditionally. #resolveHeldTurn --
  // invoked from the next speaker change or from the interrupting segment's
  // own quiet gap -- makes the actual call once the interruption has been
  // heard, and #onTranscriptDelta releases the hold early the moment the
  // interruption grows past MIN_TURN_CHARS.
  #onSpeakerChange(newRole) {
    const finished = this.openTurn
    this.openTurn = null

    if (this.heldTurn) {
      this.#resolveHeldTurn(finished, newRole)
      return
    }

    const trimmed = (finished.text || "").trim()
    if (!trimmed) return

    this.heldTurn = finished
  }

  // Resolves whatever is held in this.heldTurn against `finished`, the
  // segment that just concluded (the possible interjection). `newRole` is who
  // is about to speak next, or null when resolution is triggered by
  // `finished`'s own quiet gap with nobody else talking.
  #resolveHeldTurn(finished, newRole) {
    const held = this.heldTurn
    this.heldTurn = null
    const trimmedFinished = (finished?.text || "").trim()

    // `trimmedFinished.length` is compared without a leading truthiness guard
    // on purpose: an interrupting segment whose deltas were all whitespace
    // trims to "" and is a backchannel of length zero. Guarding on
    // truthiness first (as an earlier version did) pushed that case into the
    // commit-both branch below, splitting the held turn away from the speech
    // that resumed it -- the same corruption this method exists to prevent,
    // just triggered by a blank interjection instead of a short one. The push
    // below is what stays conditional on there being text.
    if (trimmedFinished.length < this.MIN_TURN_CHARS && newRole === held.role) {
      // Short, and the speaker we held a turn FOR is back before the gap
      // elapsed -- a backchannel, not a real turn. The held turn STARTED
      // speaking first, so it must be recorded first: committing the
      // interjection right now (before the held turn has even finished)
      // would put the model's "mm-hmm" ahead of the candidate answer it
      // interrupted in the array the scoring endpoint reads. So the
      // interjection is not committed here at all -- it is attached to the
      // held turn and reopened as this.openTurn so the resumed speech
      // continues it as ONE turn; #commitOpenTurn flushes any attached
      // interjections, in order, right after that turn's own text, once it
      // actually finishes.
      if (trimmedFinished) {
        held.pendingInterjections = held.pendingInterjections || []
        held.pendingInterjections.push({ role: finished.role, text: trimmedFinished })
      }
      this.openTurn = held
      this.#armTurnGapPoll()
      return
    }

    // Either the interjection was substantial, or its own quiet gap elapsed
    // with nobody resuming -- both are genuine, separate turns, committed in
    // the order they were actually spoken.
    this.#commitOpenTurn(held)
    if (finished) this.#commitOpenTurn(finished)
  }

  // Commits the held turn and clears the hold in one step, so no caller can
  // commit it without also releasing it (committing twice) or release it
  // without committing (losing the text). Used when the interrupting segment
  // has grown past MIN_TURN_CHARS and is therefore a genuine turn: the held
  // turn started first, so it is committed first and ordinary segmentation
  // resumes with nothing held.
  #releaseHeldTurn() {
    const held = this.heldTurn
    this.heldTurn = null
    this.#commitOpenTurn(held)
  }

  // Whatever was mid-sentence when the call ended is still worth scoring, and
  // a short interjection held while waiting to see if its speaker resumed
  // must not be silently lost just because the call ended first.
  #flushTurns() {
    const finished = this.openTurn
    this.openTurn = null
    if (this.heldTurn) {
      const held = this.heldTurn
      this.heldTurn = null
      this.#commitOpenTurn(held)
    }
    if (finished) this.#commitOpenTurn(finished)
  }

  #commitOpenTurn(turn) {
    if (!turn) return
    const trimmed = (turn.text || "").trim()
    if (trimmed) this.#pushEntry(turn.role, trimmed)

    // An interjection held mid-turn happened chronologically AFTER this
    // turn's own content started (that is exactly why it was held instead
    // of committed on the spot), so it is only ever flushed here, once the
    // turn it interrupted has actually been recorded -- never before.
    if (turn.pendingInterjections) {
      turn.pendingInterjections.forEach(({ role, text }) => this.#pushEntry(role, text))
    }
  }

  #pushEntry(role, content) {
    this.conversation.push({ role, content })
    console.log(this.LOG_PREFIX, `${role}:`, content)
    this.dispatch("turn-committed", {
      detail: { role, content, conversation: [...this.conversation] }
    })
  }

  #send(payload) {
    if (this.dc?.readyState !== "open") return false
    this.dc.send(JSON.stringify(payload))
    return true
  }

  // ── Diagnostics ───────────────────────────────────────────────────────

  // Local mic RMS. Distinguishes "the OS is giving us silence" from "we're
  // sending audio and the server is ignoring it".
  #startMicMetering(stream) {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext
      const audioContext = new AudioCtx()
      this.meterAudioContext = audioContext
      const source = audioContext.createMediaStreamSource(stream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 1024
      source.connect(analyser)

      const buffer = new Uint8Array(analyser.fftSize)
      let peak = 0
      this.micLevelTimer = setInterval(() => {
        analyser.getByteTimeDomainData(buffer)
        let sum = 0
        for (let i = 0; i < buffer.length; i++) {
          const v = (buffer[i] - 128) / 128
          sum += v * v
        }
        const rms = Math.sqrt(sum / buffer.length)
        peak = Math.max(peak, rms)
        this.dispatch("mic-level", { detail: { level: rms } })
      }, this.MIC_LEVEL_INTERVAL_MS)

      setTimeout(() => {
        console.log(
          this.LOG_PREFIX,
          `local mic peak over 5s: ${peak.toFixed(4)}` +
          (peak < 0.005 ? " — THE OS IS SENDING US SILENCE" : " — mic is picking up sound")
        )
      }, 5000)
    } catch (error) {
      console.warn(this.LOG_PREFIX, "could not meter microphone:", error)
    }
  }

  #startUplinkStatsPolling() {
    this.lastUplinkBytes = 0
    this.uplinkStatsTimer = setInterval(async () => {
      if (!this.pc || !this.sender) return
      const stats = await this.sender.getStats()
      stats.forEach((report) => {
        if (report.type === "outbound-rtp" && report.kind === "audio") {
          const deltaBytes = report.bytesSent - this.lastUplinkBytes
          this.lastUplinkBytes = report.bytesSent
          const silent = deltaBytes === 0
          console.log(
            this.LOG_PREFIX,
            `mic uplink: ${deltaBytes} bytes/2s (total ${report.bytesSent})` +
            (silent ? " — NOTHING LEAVING THE BROWSER" : "")
          )
          this.dispatch("uplink-stats", { detail: { bytesSent: report.bytesSent, deltaBytes, silent } })
        }
      })
    }, this.UPLINK_STATS_INTERVAL_MS)
  }

  // Ends the call at the cap the SERVER gave us — never a hardcoded value.
  #startSessionCapCountdown() {
    if (this.capTimer || !this.maxSessionSeconds) return
    this.secondsRemaining = this.maxSessionSeconds
    this.dispatch("time-remaining", {
      detail: { secondsRemaining: this.secondsRemaining, maxSessionSeconds: this.maxSessionSeconds }
    })
    this.capTimer = setInterval(() => {
      this.secondsRemaining = Math.max(0, this.secondsRemaining - 1)
      this.dispatch("time-remaining", {
        detail: { secondsRemaining: this.secondsRemaining, maxSessionSeconds: this.maxSessionSeconds }
      })
      if (this.secondsRemaining <= 0) this.#closeAndTeardown("session-cap")
    }, this.SESSION_CAP_TICK_MS)
  }

  // ── Teardown ──────────────────────────────────────────────────────────

  // Ask the server to close first, so final usage is collected, then tear down
  // regardless. A hung close must never leave the microphone live.
  #closeAndTeardown(reason) {
    if (this.torndown) return
    if (this.#send({ type: "session.close" })) {
      console.log(this.LOG_PREFIX, "sent session.close, waiting for session.closed")
      this.closeTimer = setTimeout(() => this.#teardown(reason), this.CLOSE_GRACE_MS)
      this.pendingCloseReason = reason
      return
    }
    this.#teardown(reason)
  }

  // Idempotent: safe from disconnect(), turbo:before-cache, end(), the cap
  // timer, session.closed, a WebRTC connection failure/close/disconnect
  // timeout, the restart-without-reload path in start(), and the start()
  // error path. If the microphone is still live after a Turbo navigation
  // away, this failed.
  #teardown(reason, { finalState = "closed" } = {}) {
    if (this.torndown) return
    this.torndown = true

    // A restart discards the previous call entirely -- it never happened as
    // far as the page is concerned -- so it must not ANNOUNCE one. Tasks
    // 9/10 bind to turn-committed and ended to score and persist the
    // transcript; without this, restarting mid-call would file the
    // abandoned call as a finished one. Every resource-releasing step below
    // still runs unconditionally -- only the two notifications are skipped.
    const isRestart = reason === "restart"

    // Whatever was mid-sentence when the call ended is still worth scoring,
    // and a short interjection held while waiting to see if its speaker
    // resumed must not be silently lost just because the call ended first.
    if (!isRestart) this.#flushTurns()

    clearInterval(this.micLevelTimer)
    clearInterval(this.uplinkStatsTimer)
    clearInterval(this.capTimer)
    clearTimeout(this.turnTimer)
    clearTimeout(this.closeTimer)
    clearTimeout(this.disconnectGraceTimer)
    this.micLevelTimer = null
    this.uplinkStatsTimer = null
    this.capTimer = null
    this.turnTimer = null
    this.closeTimer = null
    this.disconnectGraceTimer = null

    if (this.meterAudioContext && this.meterAudioContext.state !== "closed") {
      this.meterAudioContext.close().catch(() => {})
    }
    this.meterAudioContext = null

    // Detach handlers before closing. close() itself fires no state change
    // (per spec), but torndown resets to false for the very next call, so a
    // LATE state-change task somehow still queued against this now-dead
    // connection could otherwise reach into and tear down the NEXT call.
    // Latent, not demonstrated -- nulled anyway so the class of bug cannot
    // happen.
    if (this.pc) {
      this.pc.onconnectionstatechange = null
      this.pc.ontrack = null
    }
    if (this.dc) {
      this.dc.onopen = null
      this.dc.onclose = null
      this.dc.onmessage = null
    }
    try { this.dc?.close() } catch { /* already closed */ }
    try { this.pc?.close() } catch { /* already closed */ }
    this.dc = null
    this.pc = null
    this.sender = null

    this.micStream?.getTracks().forEach((track) => track.stop())
    this.micStream = null

    if (this.audioEl) {
      this.audioEl.srcObject = null
      if (this.audioElCreatedByUs) this.audioEl.remove()
      this.audioEl = null
    }

    this.listening = false
    this.speaking = false
    console.log(this.LOG_PREFIX, `torn down (${reason})`)

    this.#setConnectionState(finalState)
    if (!isRestart) this.dispatch("ended", { detail: { reason } })
  }

  #setConnectionState(state) {
    this.state = state
    this.dispatch("connection-state", { detail: { state } })
  }
}
